<!-- eslint-disable -->
<template>
  <v-row>
    <v-col cols="12" md="6">
      <!-- Change Password -->
      <v-card elevation="2" class="mb-4">
        <v-card-title>{{ $t('profile.changePassword') }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="changePassword" ref="passwordFormRef">
            <v-text-field
              v-model="passwordData.currentPassword"
              :label="$t('profile.currentPassword')"
              type="password"
              variant="outlined"
              :rules="[rules.required]"
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model="passwordData.newPassword"
              :label="$t('profile.newPassword')"
              type="password"
              variant="outlined"
              :rules="[rules.required, rules.minLength]"
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model="passwordData.confirmPassword"
              :label="$t('profile.confirmPassword')"
              type="password"
              variant="outlined"
              :rules="[rules.required, rules.passwordMatch]"
              class="mb-3"
            ></v-text-field>

            <v-alert v-if="passwordSuccess" type="success" class="mb-3" closable @click:close="passwordSuccess = ''">
              {{ passwordSuccess }}
            </v-alert>
            <v-alert v-if="passwordError" type="error" class="mb-3" closable @click:close="passwordError = ''">
              {{ passwordError }}
            </v-alert>

            <v-btn
              type="submit"
              color="primary"
              block
              :loading="changingPassword"
            >
              {{ $t('profile.changePassword') }}
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col cols="12" md="6">
      <!-- GDPR Actions -->
      <v-card elevation="2" class="mb-4">
        <v-card-title>{{ $t('common.data') }}</v-card-title>
        <v-card-text>
          <v-btn
            color="primary"
            variant="outlined"
            block
            class="mb-3"
            @click="downloadData"
            :loading="downloading"
          >
            <v-icon start>mdi-download</v-icon>
            {{ $t('profile.downloadData') }}
          </v-btn>

          <v-divider class="my-4"></v-divider>

          <v-btn
            color="error"
            variant="outlined"
            block
            @click="confirmDeleteAccount"
          >
            <v-icon start>mdi-delete-alert</v-icon>
            {{ $t('profile.deleteAccount') }}
          </v-btn>

          <v-alert type="warning" class="mt-3" density="compact">
            {{ $t('profile.deleteAccountWarning') }}
          </v-alert>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <!-- Delete Account Confirmation Dialog -->
  <confirm-dialog
    ref="deleteDialog"
    :title="$t('profile.deleteAccount')"
    :message="$t('profile.deleteAccountWarning')"
    :confirmText="$t('common.delete')"
    :cancelText="$t('common.cancel')"
    confirmColor="error"
    icon="mdi-delete-alert"
    iconColor="error"
  />
</template>

<script setup>
/* eslint-disable */
import { ref, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import api from '../services/api'
import ConfirmDialog from './ConfirmDialog.vue'

const { t } = useI18n()
const router = useRouter()
const store = useStore()

defineProps({
  profile: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['updated'])

const passwordFormRef = ref(null)
const deleteDialog = ref(null)
const changingPassword = ref(false)
const downloading = ref(false)
const passwordSuccess = ref('')
const passwordError = ref('')

const passwordData = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const rules = {
  required: v => !!v || t('validation.required'),
  minLength: v => !v || v.length >= 6 || 'Minimum 6 characters',
  passwordMatch: v => v === passwordData.newPassword || 'Passwords do not match'
}

const changePassword = async () => {
  const { valid } = await passwordFormRef.value.validate()
  if (!valid) return

  changingPassword.value = true
  passwordSuccess.value = ''
  passwordError.value = ''

  try {
    const response = await api.post('/profile/change-password', {
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
      confirmPassword: passwordData.confirmPassword
    })

    if (response.data.success) {
      passwordSuccess.value = t('profile.passwordChanged')
      passwordData.currentPassword = ''
      passwordData.newPassword = ''
      passwordData.confirmPassword = ''
      passwordFormRef.value.reset()
    }
  } catch (error) {
    console.error('Error changing password:', error)
    passwordError.value = error.response?.data?.error || t('common.error')
  } finally {
    changingPassword.value = false
  }
}

const downloadData = async () => {
  downloading.value = true
  try {
    const response = await api.get('/users?export=true')

    // Create download link
    const dataStr = JSON.stringify(response.data, null, 2)
    const blob = new Blob([dataStr], { type: 'application/json' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `my_data_${Date.now()}.json`)
    document.body.appendChild(link)
    link.click()
    link.remove()
  } catch (error) {
    console.error('Error downloading data:', error)
    alert(t('common.error'))
  } finally {
    downloading.value = false
  }
}

const confirmDeleteAccount = async () => {
  const confirmed = await deleteDialog.value.open()
  if (!confirmed) return

  // Prompt for password
  const password = prompt('Enter your password to confirm account deletion:')
  if (!password) return

  try {
    const response = await api.delete('/users', {
      data: { password }
    })

    if (response.data.success) {
      store.dispatch('auth/logout')
      router.push('/')
    }
  } catch (error) {
    console.error('Error deleting account:', error)
    alert(error.response?.data?.error || t('common.error'))
  }
}
</script>

<style scoped>
</style>
